import { FormProps } from 'antd';
import {
  ConnectPartPostError,
  ConnectPartPostParams,
  ConnectPartPostResponse,
} from '@/api/model/authentication/investor.ts';
import { useConnectPartPost } from '@/api/services/authentication/investor.ts';
import {
  PartnersPostError,
  PartnersPostParams,
  PartnersPostResponse,
} from '@/api/model/authentication/partners.ts';
import { usePartnersPost } from '@/api/services/authentication/partners.ts';
import useAuthNavigator from '@/pages/authentication/navigation/useAuthNavigator.ts';
import { AxiosError, AxiosResponse } from 'axios';
import { usePortfolioConnectPost } from '@/api/services/authentication/portfolio.ts';
import { useMessage } from '@/contexts/message/MessageContext.ts';
import { PortfolioConnectPostResponse } from '@/api/model/authentication/portfolio.ts';

export interface LoginFieldType {
  email?: string;
  password?: string;
  sec_token?: string;
}

const useLoginForm = () => {
  const { errorMessage } = useMessage();
  const { mutate: postConnectPart } = useConnectPartPost();
  const { mutate: postPartners } = usePartnersPost();
  const { mutate: postPortfolio } = usePortfolioConnectPost();
  const { userType } = useAuthNavigator();

  const isPartners = userType === 'partner';
  const isPortfolio = userType === 'portfolio';

  const onSuccessLogin = (
    response: AxiosResponse<
      | PortfolioConnectPostResponse
      | PartnersPostResponse
      | ConnectPartPostResponse
    >,
  ) => {
    document.location.href = response.data.redirect_url;
  };

  const onErrorLogin = (
    err: AxiosError<PartnersPostError | ConnectPartPostError>,
  ) => {
    errorMessage({ content: err?.response?.data.error_message });
  };

  const onFinish: FormProps<LoginFieldType>['onFinish'] = (
    values: Partial<ConnectPartPostParams | PartnersPostParams>,
  ) => {
    if (!values.email || !values.password || !values.sec_token) {
      return;
    }

    const loginParams: ConnectPartPostParams | PartnersPostParams = {
      email: values.email,
      password: values.password,
      sec_token: values.sec_token,
      sendLogin: 'login',
    };

    if (isPartners) {
      postPartners(loginParams, {
        onSuccess: onSuccessLogin,
        onError: onErrorLogin,
      });
      return;
    }

    if (isPortfolio) {
      postPortfolio(loginParams, {
        onSuccess: onSuccessLogin,
        onError: onErrorLogin,
      });
      return;
    }

    postConnectPart(loginParams, {
      onSuccess: onSuccessLogin,
      onError: onErrorLogin,
    });
  };

  return { onFinish };
};

export default useLoginForm;
